import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from '@apollo/client';
import { CREATE_CART } from '../../../../server';
import { useWindow } from '../../../../effects';
import { modernVersionBridge } from '../../../utils';
import { Cart } from '@hiwaldo/sdk/interfaces';
import {
  cartSlice,
  customerSlice,
  orderDataSlice,
  StoreState,
} from '../../../redux';

type CreateCartEffectReturn = {
  createCart: (
    callback?: (variables: any, cartId: number) => void,
    variables?: any,
    patientId?: number,
    forceNew?: boolean
  ) => void;
  loading: boolean;
  success: boolean;
  error: string | undefined;
};

export const useCreateCartEffect = (
  callback?: (data?: any) => void
): CreateCartEffectReturn => {
  const dispatch = useDispatch();
  const { region } = useWindow();

  const { cartState, customerState, basketDataState, orderDataState } =
    useSelector((state: StoreState) => {
      return {
        cartState: state.orders.cart,
        customerState: state.customers.customer,
        basketDataState: state.data.basket,
        orderDataState: state.data.order,
      };
    });

  const [doCreateCart, { loading, called, error }] = useMutation(CREATE_CART, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      dispatch(cartSlice.actions.createCart(data.createCart));
      modernVersionBridge.basket.updateCart(
        dispatch,
        basketDataState,
        data.createCart as Cart
      );
      if (callback) {
        callback(data.createCart);
      }
    },
  });

  const createCart = (
    cartCallback?: (variables: any, cartId: number) => void,
    variables?: any,
    patientId?: number,
    forceNew?: boolean
  ): void => {
    if (!cartState.cart || forceNew) {
      modernVersionBridge.basket.loading(dispatch, basketDataState, true);
      dispatch(customerSlice.actions.updateConverted());
      dispatch(
        orderDataSlice.actions.update({
          ...orderDataState,
          latestOrder: undefined,
        })
      );

      const customerId = customerState.customer
        ? customerState.customer.id
        : patientId
        ? patientId
        : undefined;
      const data = { region, customerId };

      doCreateCart({ variables: { data } }).then((response) => {
        if (cartCallback && variables) {
          cartCallback(variables, response.data.createCart.id);
        }
      });
    } else if (callback) {
      callback(cartState.cart);
    }
  };

  const success = !loading && called && !error;

  return { createCart, loading, success, error: error?.message };
};
